import { useEffect, useState } from 'react';
import { Card, Checkbox, Field, SaveBar, Switch, useToast } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useI18n } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import { useSaveSettings } from './hooks.js';

/** The 24-hour reminder, in minutes. The only offset the UI offers: a second
 * one doubles the bill, and nothing so far says it doubles the value. */
const REMINDER_24H = 1440;

/**
 * Client-facing SMS (doc 21): what the business's customers receive when they
 * book, when a booking is cancelled, and the day before.
 *
 * There is no sender-name field. Messages go out under the platform sender and
 * name the business in the body, which is a decision rather than a gap: the
 * sender field has to be registered with the Polish networks one name at a
 * time (doc 21a §3), so a box an owner could type into would promise something
 * only a support ticket can deliver.
 */
export function NotificationsCard() {
  const { t } = useI18n();
  const business = useActiveBusiness();
  const save = useSaveSettings(business.id);
  const { toast } = useToast();

  const sms = business.notifications.sms;
  const [enabled, setEnabled] = useState(sms.enabled);
  const [onBooking, setOnBooking] = useState(sms.onBooking);
  const [onCancellation, setOnCancellation] = useState(sms.onCancellation);
  const [reminder, setReminder] = useState(sms.reminderOffsetsMin.includes(REMINDER_24H));

  // Another tab must win over a stale form.
  useEffect(() => {
    setEnabled(sms.enabled);
    setOnBooking(sms.onBooking);
    setOnCancellation(sms.onCancellation);
    setReminder(sms.reminderOffsetsMin.includes(REMINDER_24H));
  }, [sms.enabled, sms.onBooking, sms.onCancellation, sms.reminderOffsetsMin]);

  const dirty =
    enabled !== sms.enabled ||
    onBooking !== sms.onBooking ||
    onCancellation !== sms.onCancellation ||
    reminder !== sms.reminderOffsetsMin.includes(REMINDER_24H);

  const discard = () => {
    setEnabled(sms.enabled);
    setOnBooking(sms.onBooking);
    setOnCancellation(sms.onCancellation);
    setReminder(sms.reminderOffsetsMin.includes(REMINDER_24H));
  };

  return (
    <Card title={t('sms.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('sms.hint')}</p>

      <Field label={t('sms.enabled')}>
        <Switch
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          label={t('sms.enabledLabel')}
        />
      </Field>

      {/* Sub-switches stay visible when the master is off, greyed rather than
          hidden: an owner deciding whether to turn this on needs to see what
          they would be turning on. */}
      <fieldset disabled={!enabled} style={{ border: 0, padding: 0, margin: '16px 0 0' }}>
        <Checkbox
          checked={onBooking}
          onChange={(e) => setOnBooking(e.target.checked)}
          label={t('sms.onBooking')}
        />
        <p className="field-hint">{t('sms.onBookingHint')}</p>
        <Checkbox
          checked={onCancellation}
          onChange={(e) => setOnCancellation(e.target.checked)}
          label={t('sms.onCancellation')}
        />
        <p className="field-hint">{t('sms.onCancellationHint')}</p>
        <Checkbox
          checked={reminder}
          onChange={(e) => setReminder(e.target.checked)}
          label={t('sms.reminder')}
        />
        {/* Consent, not this toggle, is what actually decides a reminder — an
            owner who ticks it and sees nothing sent would reasonably call it
            broken. */}
        <p className="field-hint">{t('sms.reminderHint')}</p>
      </fieldset>

      {dirty && (
        <SaveBar
          message={t('common.unsavedChanges')}
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          saving={save.isPending}
          onDiscard={discard}
          onSave={() =>
            save.mutate(
              {
                sms: {
                  enabled,
                  onBooking,
                  onCancellation,
                  reminderOffsetsMin: reminder ? [REMINDER_24H] : [],
                },
              },
              {
                onSuccess: () => toast({ title: t('sms.saved'), tone: 'success' }),
                onError: (e) => toast({ title: friendlyError(t, e), tone: 'danger' }),
              },
            )
          }
        />
      )}
    </Card>
  );
}
